import React, { useState } from 'react';
import { InputPassword } from '@jobber/components/InputPassword';
import { showToast } from '@jobber/components/Toast';
import { Button } from '@jobber/components/Button';
import { apiPost } from '../../api-client';
import { useNavigate, useLocation } from 'react-router';
import { Heading } from '@jobber/components/Heading';
import { Link } from 'react-router-dom';
import { validatePassword, validatePasswordChar } from '../../components/Common/Validations';
import { Spinner } from '@jobber/components/Spinner';
import Footer from './Components/Footer';
import { useMutation } from '@tanstack/react-query';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');
  
  const [formValues, setFormValues] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const setValuesHandler = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const resetPasswordMutation = useMutation({
    mutationFn: (data) => apiPost(`user/reset-password`, data),
    onSuccess: (result) => {
      if (result.status) {
        showToast({ message: 'Password reset successfully. You can now login.', variation: 'success' });
        navigate('/login');
      } else {
        showToast({ message: result.error || result.message, variation: 'error' });
      }
    },
    onError: (error) => {
      showToast({ message: error.message || 'Failed to reset password', variation: 'error' });
    }
  });

  const submitHandler = async (e) => {
    e.preventDefault();
    
    if (!token) {
      showToast({ message: 'Invalid or missing reset token', variation: 'error' });
      return;
    }
    if (!formValues.newPassword || !formValues.confirmPassword) {
      showToast({ message: 'Please fill out all fields', variation: 'error' });
      return;
    }
    if (formValues.newPassword !== formValues.confirmPassword) {
      showToast({ message: 'Passwords do not match', variation: 'error' });
      return;
    }
    if (!validatePassword(formValues.newPassword)) {
      showToast({
        message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        variation: 'error'
      });
      return;
    }

    resetPasswordMutation.mutate({ 
      token, 
      newPassword: formValues.newPassword, 
      confirmPassword: formValues.confirmPassword 
    });
  };

  if (!token) {
    return (
      <div className="row text-center mt-5">
        <div className="col-12">
          <Heading level={3}>Invalid Reset Link</Heading>
          <p className="mt-3">The password reset link is invalid or missing the token.</p>
          <div className="mt-3">
            <Link to="/login">
              <Button label="Back to Login" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="text-center mb-4">
        <Heading level={1}>JOBBER X THE ROYAL GATEWAY</Heading>
      </div>
      <div className="col-md-4"></div>
      <div className="col-md-4">
        <div className="d-flex justify-content-between mb-3">
          <Heading level={3}>Reset Password</Heading>
        </div>
        <div className="card">
          <form className="card-body" onSubmit={submitHandler}>
            <InputPassword
              placeholder="New Password"
              name="newPassword"
              hasVisibility={true}
              value={formValues.newPassword}
              onChange={(value) => setValuesHandler('newPassword', value)}
              validations={{
                required: { value: true, message: 'Password is required' },
                validate: validatePasswordChar,
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                }
              }}
            />
            <div className="mt-3"></div>

            <InputPassword
              placeholder="Confirm New Password"
              name="confirmPassword"
              hasVisibility={true}
              value={formValues.confirmPassword}
              onChange={(value) => setValuesHandler('confirmPassword', value)}
              validations={{
                required: { value: true, message: 'Please confirm password' }
              }}
            />
            <div className="mt-4">
              {resetPasswordMutation.isPending ? <Spinner size="base" /> : <Button label={'Reset Password'} submit />}
            </div>
          </form>
        </div>
      </div>
      <div className="col-md-4"></div>
      <Footer />
    </div>
  );
}

export default ResetPassword;